import { ChangeDetectionStrategy, Component, OnInit, Signal, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { ChipColor } from '../../../core/models/outcome-style.model';
import { Tag } from '../../../core/models/tag.model';
import { LoggerService } from '../../../core/services/logger.service';
import { TagService } from '../../../core/services/tag.service';
import { TranslationService } from '../../../core/services/translation.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../../shared/components/modal-dialog/modal-dialog.component';
import { TagChipComponent } from '../../../shared/components/tag-chip/tag-chip.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

const CHIP_COLORS: ChipColor[] = ['primary', 'accent', 'success', 'warning', 'danger'];

interface TagFormModel {
  label: string;
  category: string;
  color: ChipColor;
}

/** Admin CRUD for the org's tag list (`config.manage-lists`) — design doc §7. */
@Component({
  selector: 'app-tags-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    BackButtonComponent,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ModalDialogComponent,
    TagChipComponent,
    TranslatePipe,
  ],
  templateUrl: './tags-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './tags-settings.component.css',
})
export class TagsSettingsComponent implements OnInit {
  private tagService = inject(TagService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('tags-settings');

  tags!: Signal<Tag[]>;

  showEditor = signal(false);
  editing = signal<Tag | null>(null);
  saving = signal(false);
  error = signal<string | null>(null);

  form = new FormGroup({});
  model: TagFormModel = { label: '', category: '', color: 'primary' };
  fields: FormlyFieldConfig[] = [];

  ngOnInit(): void {
    this.tags = this.tagService.createQuery().data;

    const t = (key: string) => this.translate.translate(key);
    this.fields = [
      { key: 'label', type: 'text', props: { label: t('settings.tags.form.label'), required: true } },
      { key: 'category', type: 'text', props: { label: t('settings.tags.form.category') } },
      {
        key: 'color',
        type: 'native-select',
        props: {
          label: t('settings.tags.form.color'),
          showLabel: true,
          options: CHIP_COLORS.map((color) => ({ value: color, label: t(`settings.tags.color.${color}`) })),
        },
      },
    ];
  }

  openCreate(): void {
    this.editing.set(null);
    this.model = { label: '', category: '', color: 'primary' };
    this.error.set(null);
    this.showEditor.set(true);
  }

  openEdit(tag: Tag): void {
    this.editing.set(tag);
    this.model = { label: tag.label, category: tag.category, color: tag.color };
    this.error.set(null);
    this.showEditor.set(true);
  }

  closeEditor(): void {
    this.showEditor.set(false);
  }

  async save(): Promise<void> {
    this.saving.set(true);
    this.error.set(null);
    try {
      const existing = this.editing();
      if (existing) {
        await this.tagService.update({ id: existing.id, ...this.model });
      } else {
        await this.tagService.create(this.model);
      }
      this.showEditor.set(false);
    } catch (err) {
      this.log.error('Failed to save tag', err);
      this.error.set(this.translate.translate('settings.tags.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  async toggleActive(tag: Tag): Promise<void> {
    try {
      await this.tagService.update({ id: tag.id, is_active: tag.is_active ? 0 : 1 });
    } catch (err) {
      this.log.error('Failed to toggle tag active state', err);
    }
  }
}
